import React, { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { PiPlusLight, PiXLight } from 'react-icons/pi';
import Dropdown, { DropdownOption } from '../shared/TailwindComponents/Dropdown';
import Button from '../shared/TailwindComponents/Button';
import { Well } from '../shared/brand/Field';
import { ClientSelectorClient } from '../shared/ClientSelector';

/**
 * The Contacts section of the matter wizard.
 *
 * A matter has contacts, and a contact is set as the client (or another role)
 * on it - so this speaks of contacts throughout, where the shared
 * ClientSelector it replaces here still says "clients" and carries its own
 * icon heading under the section's heading. The value shape is unchanged
 * (`ClientSelectorClient[]`), so the payload the wizard submits is too.
 *
 * "New contact" opens the existing create-contact side panel through the
 * parent; the created contact comes back as `autoAddClient` and is added here.
 */
interface MatterContactsFieldProps {
  value?: ClientSelectorClient[];
  onChange: (contacts: ClientSelectorClient[]) => void;
  clientOptions: DropdownOption[];
  roleOptions: DropdownOption[];
  onAddNewClient?: () => void;
  autoAddClient?: ClientSelectorClient | null;
  disabled?: boolean;
}

const MatterContactsField: React.FC<MatterContactsFieldProps> = ({
  value,
  onChange,
  clientOptions,
  roleOptions,
  onAddNewClient,
  autoAddClient,
  disabled,
}) => {
  const { t } = useTranslation();
  const contacts = useMemo(() => value || [], [value]);
  const lastAutoAddedRef = useRef<string | null>(null);
  const contactsRef = useRef(contacts);
  contactsRef.current = contacts;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // A contact created from the side panel arrives here - add it once.
  useEffect(() => {
    const id = autoAddClient?.ClientID;
    if (!id || id === lastAutoAddedRef.current) return;
    lastAutoAddedRef.current = id;
    if (!contactsRef.current.some((c) => c.ClientID === id)) {
      onChangeRef.current([...contactsRef.current, autoAddClient as ClientSelectorClient]);
    }
  }, [autoAddClient]);

  const selectedIds = useMemo(() => new Set(contacts.map((c) => c.ClientID)), [contacts]);
  const available = useMemo(
    () => clientOptions.filter((opt) => !selectedIds.has(opt.value)),
    [clientOptions, selectedIds]
  );
  const labelFor = (id: string) => clientOptions.find((o) => o.value === id)?.label || id;

  const add = (id: string) => {
    if (!id || selectedIds.has(id)) return;
    onChange([...contacts, { ClientID: id, ClientRoles: [] }]);
  };
  const remove = (id: string) => onChange(contacts.filter((c) => c.ClientID !== id));
  const setRoles = (id: string, roles: string | string[]) =>
    onChange(contacts.map((c) =>
      c.ClientID === id ? { ...c, ClientRoles: Array.isArray(roles) ? roles : [roles] } : c
    ));

  const missingRole = contacts.some((c) => c.ClientID && (!c.ClientRoles || c.ClientRoles.length === 0));

  return (
    <div className="space-y-3">
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0">
          <Dropdown
            id="matter-contacts-picker"
            options={available}
            onChange={(v) => v && add(String(Array.isArray(v) ? v[0] : v))}
            placeholder={t('Select a contact')}
            width="w-full"
          />
        </div>
        {onAddNewClient && (
          <Button
            type="button"
            variant="outline"
            onClick={onAddNewClient}
            disabled={disabled}
            className="flex-shrink-0"
          >
            <PiPlusLight className="h-4 w-4" /> {t('New contact')}
          </Button>
        )}
      </div>

      {contacts.length > 0 && (
        <div className="space-y-2">
          {contacts.map((c) => (
            <Well key={c.ClientID} className="flex items-center justify-between gap-3 !py-2">
              <span className="text-sm text-fg-body truncate min-w-0">{labelFor(c.ClientID)}</span>
              <div className="flex items-center gap-2 flex-shrink-0">
                <Dropdown
                  id={`matter-contact-role-${c.ClientID}`}
                  options={roleOptions}
                  onSelect={(opt) => setRoles(c.ClientID, opt.value)}
                  value={c.ClientRoles}
                  placeholder={t('Role')}
                  width="w-36"
                  thinner
                />
                <Button
                  type="button"
                  variant="tertiary"
                  size="small"
                  onClick={() => remove(c.ClientID)}
                  ariaLabel={t('Remove contact')}
                  disabled={disabled}
                >
                  <PiXLight className="h-3.5 w-3.5" />
                </Button>
              </div>
            </Well>
          ))}
        </div>
      )}

      {missingRole && (
        <p className="text-xs text-fg-muted">
          {t('A contact without a role gets the default role. You can change it later.')}
        </p>
      )}
    </div>
  );
};

export default MatterContactsField;
